import { ChatCircleText, ShieldCheck, ShieldWarning, Sparkle, X } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, Tabs } from 'radix-ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermission } from '@/features/auth/session';
import { errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { aiApi, aiKeys, isAiUnavailable } from './api';
import { DraftsPanel } from './drafts-panel';
import { AiUnavailable } from './notices';
import { SearchPanel } from './search-panel';

/** Botão do cabeçalho + gaveta do assistente. Só aparece com `ai.use`. */
export function AiAssistantButton() {
  const allowed = usePermission('ai.use');
  const canChat = usePermission('ai.assistant');
  const [open, setOpen] = useState(false);
  if (!allowed && !canChat) return null;
  // só o assistente conversacional: o botão leva direto para a página dele
  if (!allowed) {
    return (
      <Button asChild variant="ghost" size="icon" className="size-11 sm:size-10">
        <Link to="/assistente" aria-label="Assistente de IA">
          <Sparkle className="size-5" aria-hidden />
        </Link>
      </Button>
    );
  }
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label="Assistente de IA" className="size-11 sm:size-10">
          <Sparkle className="size-5" aria-hidden />
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="oc-fade-in fixed inset-0 z-50 bg-[oklch(0.2_0.03_205/0.55)]" />
        <Dialog.Content className="oc-slide-in-right fixed bottom-0 right-0 top-[var(--imp-h,0px)] z-50 flex w-full flex-col bg-card text-card-foreground shadow-[0_24px_60px_-20px_oklch(0.2_0.05_205/0.5)] sm:w-[30rem] sm:max-w-[calc(100vw-2rem)] sm:border-l">
          <header className="flex items-start justify-between gap-3 border-b px-4 py-4 sm:px-5">
            <div className="min-w-0">
              <Dialog.Title className="flex items-center gap-2 font-display text-lg leading-tight font-semibold">
                <Sparkle className="size-5 text-primary" weight="duotone" aria-hidden />
                Assistente de IA
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-muted-foreground">Ajuda administrativa. Tudo que ele gera é rascunho para você revisar.</Dialog.Description>
            </div>
            <Dialog.Close aria-label="Fechar" className="-mt-1 -mr-2 flex size-11 shrink-0 items-center justify-center rounded-control text-muted-foreground hover:bg-muted hover:text-foreground">
              <X className="size-5" aria-hidden />
            </Dialog.Close>
          </header>
          {canChat && (
            <Link
              to="/assistente"
              onClick={() => setOpen(false)}
              className="flex min-h-11 items-center gap-2 border-b bg-primary/5 px-4 text-sm font-medium text-primary hover:bg-primary/10 sm:px-5"
            >
              <ChatCircleText className="size-4" aria-hidden />
              Abrir o assistente de conversa
            </Link>
          )}
          <AssistantBody />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const TABS = [
  { value: 'rascunhos', label: 'Rascunhos' },
  { value: 'busca', label: 'Busca' },
] as const;

function AssistantBody() {
  const status = useQuery({ queryKey: aiKeys.status, queryFn: aiApi.status, retry: false });
  const [unavailable, setUnavailable] = useState(false);

  if (status.isPending) {
    return (
      <div className="flex flex-col gap-3 p-5" role="status" aria-label="Carregando assistente">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-24" />
      </div>
    );
  }
  const off = unavailable || (status.isError ? isAiUnavailable(status.error) : !status.data.available);
  if (off) {
    return (
      <div className="flex-1 overflow-y-auto p-5">
        <AiUnavailable />
      </div>
    );
  }
  if (status.isError) {
    return (
      <div className="flex flex-1 flex-col items-start gap-3 p-5" role="alert">
        <p className="text-sm text-muted-foreground">{errorMessage(status.error)}</p>
        <Button variant="secondary" size="lg" onClick={() => void status.refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  const s = status.data;
  const onUnavailable = () => setUnavailable(true);
  return (
    <Tabs.Root defaultValue="rascunhos" className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col gap-1.5 border-b bg-muted/50 px-4 py-3 text-xs text-muted-foreground sm:px-5">
        <p className="flex items-start gap-1.5">
          {s.externalEnabled ? <ShieldWarning className="mt-0.5 size-4 shrink-0 text-warning" weight="bold" aria-hidden /> : <ShieldCheck className="mt-0.5 size-4 shrink-0 text-success" weight="bold" aria-hidden />}
          <span>
            {s.externalEnabled
              ? 'Envio de dados identificáveis: ligado. Só vão dados de clientes que deram o consentimento de IA externa.'
              : 'Envio de dados identificáveis: desligado. A IA recebe apenas dados sem identificação.'}
          </span>
        </p>
      </div>
      <Tabs.List aria-label="Funções do assistente" className="flex gap-1 overflow-x-auto overflow-y-hidden border-b px-2 sm:px-3">
        {TABS.map((t) => (
          <Tabs.Trigger
            key={t.value}
            value={t.value}
            className={cn(
              'relative min-h-11 shrink-0 px-3 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground',
              'data-[state=active]:text-primary data-[state=active]:after:absolute data-[state=active]:after:inset-x-2 data-[state=active]:after:bottom-0 data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-full data-[state=active]:after:bg-primary',
              'rounded-control focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
            )}
          >
            {t.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-5">
        <Tabs.Content value="rascunhos" className="focus-visible:outline-none">
          <DraftsPanel onUnavailable={onUnavailable} />
        </Tabs.Content>
        <Tabs.Content value="busca" className="focus-visible:outline-none">
          <SearchPanel onUnavailable={onUnavailable} />
        </Tabs.Content>
      </div>
    </Tabs.Root>
  );
}
